import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { EmptyStateTitle } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

interface BriefTemplatesNoResultsStateProps {
  /**
   * Fires when the user clicks "Clear search". When omitted, no button is shown.
   */
  onClear?: () => void;
  /** A level or status filter is part of what emptied the list, so the copy names filters. */
  filtersActive?: boolean;
}

/**
 * "No search results" state for the Brief Templates list view. Iconless +
 * vertically centered, matching the Projects list no-results state.
 *
 * Rendered by `BriefTemplatesList` when the templates query resolved with
 * a NON-empty list but the active (debounced) search term filters all rows
 * out. Distinct copy from the initial empty state; offers a "Clear search"
 * button (parity with the Projects "Clear filters" action).
 *
 * Pure presentational. `role="status"` so screen readers announce the
 * state change.
 */
export function BriefTemplatesNoResultsState({
  onClear,
  filtersActive = false,
}: BriefTemplatesNoResultsStateProps = {}) {
  const { t } = useTranslation('knowledge');

  return (
    <Empty role="status" data-testid="brief-templates-no-results-state" className="flex-1">
      <EmptyHeader>
        <EmptyStateTitle>
          {t('knowledgeHub.briefTemplatesNoResultsState.noMatchingTemplates')}
        </EmptyStateTitle>
        <EmptyDescription>
          {filtersActive
            ? t('knowledgeHub.briefTemplatesNoResultsState.tryOtherFilters')
            : t('knowledgeHub.briefTemplatesNoResultsState.tryADifferentSearchTerm')}
        </EmptyDescription>
      </EmptyHeader>
      {onClear ? (
        <EmptyContent>
          <Button variant="outline" onClick={onClear}>
            {filtersActive
              ? t('knowledgeHub.briefTemplatesNoResultsState.clearFilters')
              : t('knowledgeHub.briefTemplatesNoResultsState.clearSearch')}
          </Button>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
